import { useState, type ImgHTMLAttributes, type CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { ImageOff } from 'lucide-react';
import { useMediaQuery } from '../lib/useMediaQuery';

// ─── ProxiedImage ────────────────────────────────────────────────
// The <img> slot for ReactMarkdown (and a reusable responsive primitive). It
// does NOT mint tokens — when the server has rewritten the markdown image to a
// signed /api/img URL it just renders it; otherwise it renders the original
// URL with the same lazy-loading + premium styling (graceful fallback).
//
// Behaviour: lazy + async decode, no-layout-shift (aspect-ratio when known),
// fade-in on load (respecting prefers-reduced-motion), and a compact fallback
// tile on error (no retry loop).

/** Mirrors the server `sizes` so markdown images (no srcset) stay responsive. */
const DEFAULT_SIZES = '(max-width: 1024px) 92vw, 1000px';

type ProxiedImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  /** Injected by react-markdown — destructured out so it never hits the DOM. */
  node?: unknown;
};

export function ProxiedImage({
  node: _node,
  src,
  srcSet,
  sizes,
  alt,
  title,
  width,
  height,
  className,
  style,
}: ProxiedImageProps) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  if (!src) return null;

  if (status === 'error') {
    return (
      <span className="proxied-image-error" role="img" aria-label={alt || 'Image unavailable'} title={title}>
        <ImageOff size={15} aria-hidden="true" />
        <span>{alt || 'Image unavailable'}</span>
      </span>
    );
  }

  const w = typeof width === 'string' ? parseInt(width, 10) : width;
  const h = typeof height === 'string' ? parseInt(height, 10) : height;
  const aspect: CSSProperties = w && h ? { aspectRatio: `${w} / ${h}` } : {};

  return (
    <motion.img
      src={src}
      srcSet={srcSet}
      sizes={sizes ?? DEFAULT_SIZES}
      alt={alt ?? ''}
      title={title}
      width={width}
      height={height}
      loading="lazy"
      decoding="async"
      className={className ? `proxied-image ${className}` : 'proxied-image'}
      style={{ ...aspect, ...style }}
      initial={{ opacity: reduceMotion ? 1 : 0 }}
      animate={{ opacity: status === 'loaded' || reduceMotion ? 1 : 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.25, ease: 'easeOut' }}
      onLoad={() => setStatus('loaded')}
      onError={() => setStatus('error')}
    />
  );
}

export default ProxiedImage;
